import { useCallback, useRef } from 'react';
import { endpoints } from '@/api';
import type { AdsConfig, Me, Session } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { useModeration } from '@/stores/moderation';
import { useAdOverlay } from './overlay';
import { adsOnFor, getAdProvider, peekAdProvider } from './runtime';
import { safeStorage, utcDay } from './storage';
import { useAdsConfig } from './useAds';

/** Total wait for an interstitial to be ready before we just navigate (SPEC_V5 §3). */
export const INTERSTITIAL_WAIT_MS = 5000;
const KEY = 'popin.ads.betweenCalls';

export interface BetweenCallsLocal {
  /** Started calls since the last between-calls ad (synced to the server's count). */
  calls: number;
  /** Session ids already counted (last few). */
  counted: string[];
  lastShownAt: number | null;
  day: string;
  shownToday: number;
}

let memory: BetweenCallsLocal | null = null;

export function readLocal(now = Date.now()): BetweenCallsLocal {
  const fresh: BetweenCallsLocal = { calls: 0, counted: [], lastShownAt: null, day: utcDay(now), shownToday: 0 };
  const s = safeStorage.getJSON<Partial<BetweenCallsLocal>>(KEY) ?? memory ?? fresh;
  const v: BetweenCallsLocal = {
    calls: typeof s.calls === 'number' && s.calls >= 0 ? s.calls : 0,
    counted: Array.isArray(s.counted) ? s.counted.filter((x): x is string => typeof x === 'string').slice(-20) : [],
    lastShownAt: typeof s.lastShownAt === 'number' ? s.lastShownAt : null,
    day: typeof s.day === 'string' ? s.day : fresh.day,
    shownToday: typeof s.shownToday === 'number' ? s.shownToday : 0,
  };
  if (v.day !== fresh.day) return { ...v, day: fresh.day, shownToday: 0 };
  return v;
}

function writeLocal(v: BetweenCallsLocal) {
  memory = v;
  safeStorage.setJSON(KEY, v);
}

/** Count a finished call once (only calls that actually started). */
export function countCall(s: Pick<Session, 'id' | 'started_at'>): BetweenCallsLocal {
  const v = readLocal();
  if (!s.started_at || v.counted.includes(s.id)) return v;
  const next = { ...v, calls: v.calls + 1, counted: [...v.counted, s.id].slice(-20) };
  writeLocal(next);
  return next;
}

function markShown(now = Date.now()) {
  const v = readLocal(now);
  writeLocal({ ...v, calls: 0, lastShownAt: now, shownToday: v.shownToday + 1 });
}

function syncCalls(n: number) {
  writeLocal({ ...readLocal(), calls: Math.max(0, Math.floor(n)) });
}

const PROTECTED_ENDS = new Set(['report', 'block', 'moderation', 'no_face', 'safety']);

/**
 * Never right after a call that ended for safety reasons, a report or no_face,
 * and never when they just added a friend — protect that moment (SPEC_V5 §3).
 */
export function protectedMoment(s: Session): boolean {
  if (!s.started_at) return true; // nothing happened; not a "between calls" moment
  if (s.end_reason && PROTECTED_ENDS.has(s.end_reason)) return true;
  if (s.connected) return true;
  if (s.my_feedback?.decision === 'connect') return true;
  return false;
}

/** Client mirror of the server caps (the server stays authoritative). */
export function clientCapsAllow(caps: AdsConfig['caps'], me: Me, local: BetweenCallsLocal, now = Date.now()): { ok: boolean; reason?: string } {
  const created = Date.parse(me.created_at);
  if (caps.first_day_free && !Number.isNaN(created) && now - created < 24 * 3600_000) return { ok: false, reason: 'first_day' };
  if (local.shownToday >= caps.interstitial_daily_max) return { ok: false, reason: 'daily_cap' };
  if (local.lastShownAt !== null && now - local.lastShownAt < caps.interstitial_min_gap_s * 1000) return { ok: false, reason: 'min_gap' };
  if (local.calls < caps.interstitial_every_n_calls) return { ok: false, reason: 'every_n_calls' };
  return { ok: true };
}

function within<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(null), Math.max(0, ms));
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(null);
      },
    );
  });
}

export interface BetweenCallsResult {
  shown: boolean;
  reason?: string;
}

/**
 * The interstitial at a natural break (SPEC_V5 §3). Call from the "Next
 * person" / "Back home" handlers with the call that just ended and the
 * navigation to perform. `go` always runs exactly once — on success, on any
 * failure, or straight away when no ad applies (Plus, ads off, caps, protected
 * moment). Navigation is never blocked by the ad provider.
 */
export async function runBetweenCalls(ads: AdsConfig | null, me: Me | null, session: Session | null | undefined, go: () => void): Promise<BetweenCallsResult> {
  let navigated = false;
  const nav = () => {
    if (navigated) return;
    navigated = true;
    useAdOverlay.getState().set(null);
    go();
  };
  try {
    const local = session ? countCall(session) : readLocal();
    if (!adsOnFor(ads, me) || !ads.units.interstitial) return nav(), { shown: false, reason: 'off' };
    const mod = useModeration.getState();
    if (mod.blocked || mod.closed) return nav(), { shown: false, reason: 'blocked' };
    if (!session || session.state !== 'ended') return nav(), { shown: false, reason: 'not_ended' };
    if (protectedMoment(session)) return nav(), { shown: false, reason: 'protected' };
    const gate = clientCapsAllow(ads.caps, me!, local);
    if (!gate.ok) return nav(), { shown: false, reason: gate.reason };

    const deadline = Date.now() + INTERSTITIAL_WAIT_MS;
    const left = () => deadline - Date.now();
    const elig = await within(endpoints.adsEligibility({ placement: 'between_calls', session_id: session.id, calls_since_last: local.calls }), left());
    if (!elig) return nav(), { shown: false, reason: 'eligibility_unavailable' };
    if (typeof elig.calls_since_last === 'number') syncCalls(elig.calls_since_last);
    if (!elig.eligible) return nav(), { shown: false, reason: elig.reason };
    if (navigated) return { shown: false, reason: 'skipped' };

    useAdOverlay.getState().set({ kind: 'interstitial', phase: 'loading' });
    const provider = await within(getAdProvider(ads), left());
    if (!provider || navigated) return nav(), { shown: false, reason: 'no_provider' };
    // Once visible the person closes it themselves; the hard cap only guards a stuck provider.
    const shown = await within(provider.showInterstitial('between_calls', { loadTimeoutMs: Math.max(0, left()) }), Math.max(0, left()) + 90_000);
    if (shown !== 'shown') return nav(), { shown: false, reason: 'unavailable' };
    markShown();
    void endpoints.adsImpression('between_calls').catch(() => undefined);
    nav();
    return { shown: true };
  } catch {
    nav();
    return { shown: false, reason: 'error' };
  } finally {
    nav();
  }
}

/** Skip button on the interstitial chrome: leave now, the ad is abandoned. */
let skipCurrent: (() => void) | null = null;
export function skipBetweenCallsAd(): void {
  skipCurrent?.();
}

export function useBetweenCallsAd(): (session: Session | null | undefined, go: () => void) => Promise<BetweenCallsResult> {
  const ads = useAdsConfig();
  const running = useRef(false);
  return useCallback(
    async (session, go) => {
      if (running.current) return { shown: false, reason: 'busy' };
      running.current = true;
      let left = false;
      const once = () => {
        if (left) return;
        left = true;
        skipCurrent = null;
        go();
      };
      skipCurrent = () => {
        useAdOverlay.getState().set(null);
        peekAdProvider()?.destroyAll();
        once();
      };
      try {
        return await runBetweenCalls(ads, useAuth.getState().me, session, once);
      } finally {
        running.current = false;
        skipCurrent = null;
      }
    },
    [ads],
  );
}
